// components/TwoFactorAuthModal.jsx
import React, { useState, useRef, useEffect } from 'react';
import TwoFactorSuccessModal from './TwoFactorSuccessModal';

// Helper to get token from cookies
const getTokenFromCookie = () => {
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'accessToken' || name === 'access_token') {
      return decodeURIComponent(value);
    }
  }
  return null;
};

const TwoFactorAuthModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [step, setStep] = useState('email');
  const [code, setCode] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [otpToken, setOtpToken] = useState(''); // ✅ NEW: Store OTP token
  
  const inputRefs = useRef([]);

  useEffect(() => {
    if (!isOpen) {
      setEmail('');
      setStep('email');
      setCode(['', '', '', '', '', '']);
      setError('');
      setOtpToken(''); // ✅ Reset OTP token
    }
  }, [isOpen]);

  const handleSendCode = async () => {
    setError('');
    
    if (!email) {
      setError('Please enter your email address');
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    setLoading(true);

    try {
      const token = getTokenFromCookie();
      
      console.log('Token found:', token ? 'YES' : 'NO');
      
      if (!token) {
        setError('You need to be logged in to enable 2FA');
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/2fa/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ email })
      });

      const data = await response.json();

      if (response.ok) {
        // ✅ NEW: Store the OTP token from response
        if (data.data?.otp_token) {
          setOtpToken(data.data.otp_token);
          console.log('OTP token received:', data.data.otp_token.substring(0, 20) + '...');
        } else {
          setError('Failed to receive OTP token. Please try again.');
          setLoading(false);
          return;
        }
        
        setStep('verify');
        setError('');
      } else {
        setError(data.message || 'Failed to send verification code');
      }
    } catch (err) {
      console.error('2FA send error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    setError('');
    
    const codeString = code.join('');
    
    if (codeString.length !== 6) {
      setError('Please enter all 6 digits');
      return;
    }

    // ✅ NEW: Check if OTP token exists
    if (!otpToken) {
      setError('Session expired. Please request a new code.');
      return;
    }

    setLoading(true);

    try {
      const token = getTokenFromCookie();
      
      if (!token) {
        setError('You need to be logged in to verify 2FA');
        setLoading(false);
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/2fa/verify`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          code: codeString,
          otp_token: otpToken // ✅ NEW: Send OTP token
        })
      });

      const data = await response.json();

      console.log('Verify response:', response.ok, data);

      if (response.ok) {
        // Set success modal to true BEFORE closing main modal
        setShowSuccessModal(true);
        // Close main modal
        onClose();
      } else {
        setError(data.message || 'Invalid verification code');
        setCode(['', '', '', '', '', '']);
        inputRefs.current[0]?.focus();
      }
    } catch (err) {
      console.error('2FA verify error:', err);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (index, value) => {
    if (value && !/^\d$/.test(value)) return;

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').slice(0, 6);
    const digits = pastedData.split('').filter(char => /^\d$/.test(char));
    
    const newCode = [...code];
    digits.forEach((digit, index) => {
      if (index < 6) {
        newCode[index] = digit;
      }
    });
    setCode(newCode);
    
    const nextEmptyIndex = newCode.findIndex(val => !val);
    const focusIndex = nextEmptyIndex === -1 ? 5 : nextEmptyIndex;
    inputRefs.current[focusIndex]?.focus();
  };

  const handleResendCode = async () => {
    setError('');
    setCode(['', '', '', '', '', '']);
    await handleSendCode();
  };

  const handleBackToEmail = () => {
    setStep('email');
    setCode(['', '', '', '', '', '']);
    setError('');
    setOtpToken(''); // ✅ Clear OTP token when going back
  };

  const handleCancel = () => {
    setEmail('');
    setStep('email');
    setCode(['', '', '', '', '', '']);
    setError('');
    setOtpToken(''); // ✅ Clear OTP token on cancel
    onClose();
  };

  const handleSuccessModalClose = () => {
    setShowSuccessModal(false);
    setEmail('');
    setStep('email');
    setCode(['', '', '', '', '', '']);
    setError('');
    setOtpToken(''); // ✅ Clear OTP token on success
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-white/40 backdrop-blur-md"
            onClick={handleCancel}
          />
          
          <div className="relative bg-white rounded-2xl border-[1.5px] border-primary-blue shadow-2xl w-full max-w-[25rem] 2xl:max-w-md">
            <div className="relative px-8 pt-6 pb-5">
              <h2 className="text-xl font-semibold text-gray-900 text-center">
                {step === 'email' ? '2-Factor Authentication' : 'Verify Your Email'}
              </h2>
              
              {step === 'verify' ? (
                <button
                  onClick={handleBackToEmail}
                  className="absolute right-6 top-6 hover:opacity-70 transition-opacity"
                  disabled={loading}
                >
                  <img 
                    src="/Icons/arrow-back.svg" 
                    alt="Back"
                    className="w-6 h-6"
                  />
                </button>
              ) : (
                <button
                  onClick={handleCancel}
                  className="absolute right-6 top-6 hover:opacity-70 transition-opacity"
                  disabled={loading}
                >
                  <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" stroke="#9CA3AF" strokeWidth="2" fill="none"/>
                    <path d="M8 8L16 16M16 8L8 16" stroke="#9CA3AF" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                </button>
              )}
            </div>

            <div className="px-8 pb-6 pt-2 flex flex-col items-center">
              <div className="mb-4">
                <img
                  src={step === 'verify' ? '/Icons/email-logo.svg' : '/Icons/auth-tick.svg'}
                  alt={step === 'verify' ? 'Email Verification' : '2-Factor Authentication'}
                  className="w-20 h-20"
                />
              </div>
              
              <p className="text-center text-gray-600 text-sm mb-6">
                {step === 'email' 
                  ? 'Add your email to use 2-factor authentication to add more security to your account'
                  : (
                      <>
                        Please enter the 6 digit code sent to{' '}
                        <span className="font-semibold text-gray-900">{email}</span>
                      </>
                    )
                }
              </p>

              {error && (
                <div className="w-full p-3 bg-red-50 border border-red-200 rounded-lg text-red-600 text-sm mb-4">
                  {error}
                </div>
              )}

              <div className="w-full">
                {step === 'email' ? (
                  <>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="i.e. lambecchi@gmail.com"
                      className="w-full px-4 py-3 border-[1.5px] border-primary-blue rounded-lg focus:outline-none focus:border-2 focus:border-primary-blue transition-colors"
                      disabled={loading}
                    />
                  </>
                ) : (
                  <>
                    <div className="flex gap-2 justify-center mb-4">
                      {code.map((digit, index) => (
                        <input
                          key={index}
                          ref={el => inputRefs.current[index] = el}
                          type="text"
                          inputMode="numeric"
                          maxLength={1}
                          value={digit}
                          onChange={(e) => handleCodeChange(index, e.target.value)}
                          onKeyDown={(e) => handleKeyDown(index, e)}
                          onPaste={handlePaste}
                          className="w-12 h-14 text-center text-xl font-semibold border-[1.5px] border-primary-blue rounded-lg focus:outline-none focus:border-2 focus:border-primary-blue transition-colors"
                          disabled={loading}
                        />
                      ))}
                    </div>

                    <button
                      onClick={handleResendCode}
                      disabled={loading}
                      className="text-primary-blue text-sm font-medium hover:underline disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Resend Code
                    </button>
                  </>
                )}
              </div>
            </div>

            <div className="px-8 pb-6 pt-2 flex gap-3">
              <button
                onClick={step === 'email' ? handleSendCode : handleVerifyCode}
                disabled={loading}
                className="flex-1 py-3 bg-primary-blue text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors text-base disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading 
                  ? (step === 'email' ? 'Sending...' : 'Verifying...') 
                  : step === 'email' 
                  ? 'Send' 
                  : 'Verify'
                }
              </button>
              <button
                onClick={handleCancel}
                disabled={loading}
                className="flex-1 py-3 bg-white border-[1.5px] border-primary-blue text-primary-blue font-semibold rounded-lg hover:bg-primary-blue hover:text-white transition-colors text-base disabled:opacity-50"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      <TwoFactorSuccessModal 
        isOpen={showSuccessModal} 
        onClose={handleSuccessModalClose}
      />
    </>
  );
};

export default TwoFactorAuthModal;
